// ===============================
// PromotionsFilters.js — compact filters with date presets
// ===============================

import React from 'react';
import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { FaPlusCircle, FaMinusCircle } from 'react-icons/fa';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import DropDown from './DropDown';
import active_on from './../images/icons/active_on.png';
import active_off from './../images/icons/active_off.png';

const ExpandMore = styled((props) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

export default class PromotionsFilters extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      expanded: false,
      addNew: false,
      filtersCounter: 0,
      allowEdit: false,

      // toggles
      filters_isActive: 1,

      // enums (string codes for DropDown with hasIcons=true)
      filters_type: 'ANY',
      filters_frequency: 'ANY',
      filters_gender: 'ANY',
      filters_proximity: 'ANY',

      // date preset (single select)
      filters_datePreset: 'ANY',
      customFrom: '',
      customTo: '',

      // option maps — aligned with backend enums
      typeOptions: {
        ANY: 'Any',
        0: 'Full',
        1: 'Discount Value',
        2: 'Discount Percentage',
      },
      frequencyOptions: {
        ANY: 'Any',
        0: 'Once',
        1: 'Once A Day',
        2: 'No Limit',
      },
      genderOptions: {
        ANY: 'Any',
        1: 'Female',
        2: 'Male',
      },
      proximityOptions: {
        ANY: 'Any',
        0: 'None',
        1: 'Near',
        2: 'Far',
      },

      // date presets (we’ll compute real dates when selected)
      datePresetOptions: {
        ANY:        'Any time',
        CUSTOM:     'Custom range',
        TODAY:      'Today',
        LAST_7:     'Last 7 days',
        LAST_30:    'Last 30 days',
        NEXT_7:     'Next 7 days',
        NEXT_30:    'Next 30 days',
        THIS_MONTH: 'This month',
        PREV_MONTH: 'Previous month',
        FUTURE:     'Upcoming only',     // dateFrom >= today
        PAST:       'Expired only',      // dateTo < today
      },
    };

    // live query object sent to backend
    this.queryData = {};

    // refs for text inputs
    this.searchKeyword = React.createRef();
    this.codeRef = React.createRef();

    // binds
    this.handleExpandClick = this.handleExpandClick.bind(this);
    this.toggleAdd = this.toggleAdd.bind(this);
    this.searchKey = this.searchKey.bind(this);
    this.onCodeChange = this.onCodeChange.bind(this);
    this.toggleActive = this.toggleActive.bind(this);
    this.resetFilters = this.resetFilters.bind(this);

    this.onDropType = this.onDropType.bind(this);
    this.onDropFrequency = this.onDropFrequency.bind(this);
    this.onDropGender = this.onDropGender.bind(this);
    this.onDropProximity = this.onDropProximity.bind(this);
    this.onDropDatePreset = this.onDropDatePreset.bind(this);
    this.onCustomFromChange = this.onCustomFromChange.bind(this);
    this.onCustomToChange = this.onCustomToChange.bind(this);
  }

  componentDidMount() {
    if (this.props.allowEdit === true) this.setState({ allowEdit: true });
    // Default: show active
    this.queryData.isActive = 1;
    let today = new Date();
    let pad = (n) => String(n).padStart(2, '0');
    let currentDate = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
    this.setState ( {customFrom: currentDate});
    this.setState ( {customTo: currentDate});
    this.apply();
  }

  // ---------- small utils ----------
  ymdToDmy = (s) => {
    if (!s || s.length < 10) return '';
    const [y, m, d] = s.split('-');
    if (!y || !m || !d) return '';
    return `${d}-${m}-${y}`;
  };
  endOfDayDmy = (s) => {
    const dmy = this.ymdToDmy(s);
    return dmy ? `${dmy} 23:59:59` : '';
  };

  ddmmyyyy(date) {
    const d = new Date(date);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  }

  // compute dateFrom_gte / dateTo_lte based on a preset
  computeDateRange(preset) {
    const today = new Date();
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const endOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

    const set = (from, to) => ({
      dateFrom_gte: from ? this.ddmmyyyy(from) : undefined,
      dateTo_lte:   to   ? this.ddmmyyyy(to)   : undefined,
    });

    switch (preset) {
      case 'TODAY':      return set(startOfToday, endOfToday);
      case 'LAST_7':     return set(new Date(startOfToday.getTime() - 6*24*3600*1000), endOfToday);
      case 'LAST_30':    return set(new Date(startOfToday.getTime() - 29*24*3600*1000), endOfToday);
      case 'NEXT_7':     return set(startOfToday, new Date(startOfToday.getTime() + 6*24*3600*1000));
      case 'NEXT_30':    return set(startOfToday, new Date(startOfToday.getTime() + 29*24*3600*1000));
      case 'THIS_MONTH': {
        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        const end   = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59);
        return set(start, end);
      }
      case 'PREV_MONTH': {
        const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const end   = new Date(today.getFullYear(), today.getMonth(), 0, 23, 59, 59);
        return set(start, end);
      }
      case 'FUTURE':     return set(startOfToday, undefined);
      case 'PAST':       return set(undefined, endOfToday);
      default:           return set(undefined, undefined); // ANY
    }
  }

  handleExpandClick() {
    this.setState((s) => ({ expanded: !s.expanded }));
  }

  toggleAdd() {
    if (!this.state.allowEdit) return;
    this.setState((s) => ({ addNew: !s.addNew }));
    this.props.toggleAdd();
  }

  // apply & count
  apply() {
    // recompute visible counter (only real keys sent)
    this.setState({ filtersCounter: Object.keys(this.queryData).length });
    this.props.applyFilters(this.queryData);
  }

  // ---------- basic text filters ----------
  searchKey(e) {
    const keyword = e.target.value;
    if (keyword && keyword.length > 2) this.queryData.nameLike = keyword;
    else delete this.queryData.nameLike;
    this.apply();
  }

  onCodeChange(e) {
    const code = e.target.value;
    if (code && code.length > 1) this.queryData.vCodeLike = code;
    else delete this.queryData.vCodeLike;
    this.apply();
  }

  toggleActive() {
    const newVal = this.state.filters_isActive ? 0 : 1;
    this.setState({ filters_isActive: newVal });
    if (newVal === 1) this.queryData.isActive = 1;
    else delete this.queryData.isActive;
    this.apply();
  }

  // ---------- enum filters ----------
  onDropType(v) {
    this.setState({ filters_type: v });
    if (v === 'ANY') delete this.queryData.type;
    else this.queryData.type = v;
    this.apply();
  }

  onDropFrequency(v) {
    this.setState({ filters_frequency: v });
    if (v === 'ANY') delete this.queryData.frequency;
    else this.queryData.frequency = v;
    this.apply();
  }

  onDropGender(v) {
    this.setState({ filters_gender: v });
    if (v === 'ANY') delete this.queryData.gender;
    else this.queryData.gender = v;
    this.apply();
  }

  onDropProximity(v) {
    this.setState({ filters_proximity: v });
    if (v === 'ANY') delete this.queryData.proximity;
    else this.queryData.proximity = v;
    this.apply();
  }

  // ---------- date preset ----------
  onDropDatePreset(v) {
    this.setState({ filters_datePreset: v });

    // wipe previous range
    delete this.queryData.dateFrom_gte;
    delete this.queryData.dateTo_lte;

    if (v === 'CUSTOM') {
      // don’t compute; user will type into the two inputs
      // (we’ll apply on each input change)
      this.apply();
      return;
    }
    if (v !== 'ANY') {
      const { dateFrom_gte, dateTo_lte } = this.computeDateRange(v);
      if (dateFrom_gte) this.queryData.dateFrom_gte = dateFrom_gte;
      if (dateTo_lte)   this.queryData.dateTo_lte   = dateTo_lte;
    }

    this.apply();
  }

  onCustomFromChange = (e) => {
    const ymd = e.target.value; // "YYYY-MM-DD"
    this.setState({ customFrom: ymd });

    // backend wants "dd-mm-YYYY"
    const dmy = this.ymdToDmy(ymd);
    if (dmy) this.queryData.dateFrom_gte = dmy;
    else delete this.queryData.dateFrom_gte;

    this.apply();
  };

  onCustomToChange = (e) => {
    const ymd = e.target.value; // "YYYY-MM-DD"
    this.setState({ customTo: ymd });

    // make the TO inclusive -> end of day "dd-mm-YYYY 23:59:59"
    const dmyEnd = this.endOfDayDmy(ymd);
    if (dmyEnd) this.queryData.dateTo_lte = dmyEnd;
    else delete this.queryData.dateTo_lte;

    this.apply();
  };

  // ---------- reset ----------
  resetFilters() {
    this.queryData = {};
    if (this.searchKeyword.current) this.searchKeyword.current.value = '';
    if (this.codeRef.current) this.codeRef.current.value = '';
    this.setState({
      filters_isActive: 0,
      filtersCounter: 0,
      filters_type: 'ANY',
      filters_frequency: 'ANY',
      filters_gender: 'ANY',
      filters_proximity: 'ANY',
      filters_datePreset: 'ANY',
    });
    let today = new Date();
    let pad = (n) => String(n).padStart(2, '0');
    let currentDate = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
    this.setState ( {customFrom: currentDate});
    this.setState ( {customTo: currentDate});
    this.props.applyFilters(this.queryData);
  }

  render() {
    // Compact, responsive two-row layout that wraps naturally
    return (
      <Card sx={{ borderRadius: '16px', m: 1, backgroundColor: '#214a80' }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          {/* Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton aria-label="Add Promotion" title="Add Promotion" onClick={this.toggleAdd}>
              <Avatar alt="Add Promotion" sx={{ width: 24, height: 24, color: '#273442' }}>
                {this.state.addNew ? <FaMinusCircle /> : <FaPlusCircle />}
              </Avatar>
            </IconButton>

            <IconButton aria-label="Active" title="Active" onClick={this.toggleActive}>
              <Avatar alt="Active" src={this.state.filters_isActive ? active_on : active_off} sx={{ width: 24, height: 24 }} />
            </IconButton>
          </Box>

          {/* Text filters */}
          <TextField
            id="search-name"
            inputRef={this.searchKeyword}
            label="Name contains"
            variant="standard"
            onChange={this.searchKey}
            InputLabelProps={{ sx: { color: '#e3e3e5' } }}
            inputProps={{ sx: { color: '#e3e3e5' } }}
            sx={{ color: "#e3e3e5", marginLeft: "32px" }}
          />

          <TextField
            id="search-code"
            inputRef={this.codeRef}
            label="Code contains"
            variant="standard"
            onChange={this.onCodeChange}
            InputLabelProps={{ sx: { color: '#e3e3e5' } }}
            inputProps={{ sx: { color: '#e3e3e5' } }}
            sx={{ color: "#e3e3e5", marginLeft: "32px" }}
          />

          {/* Enum filters */}
          <DropDown name="Type"
            value={String(this.state.filters_type)}
            doptions={this.state.typeOptions}
            hasIcons={false}
            minWidth={100}
            onDark={false}
            onChangeFunction={this.onDropType}
            size="small"
            marginTop={8}
          />

          <DropDown name="Frequency"
            value={String(this.state.filters_frequency)}
            doptions={this.state.frequencyOptions}
            hasIcons={false}
            minWidth={100}
            onDark={false}
            onChangeFunction={this.onDropFrequency}
            size="small"
            marginTop={8}
            marginLeft={2}
          />

          <DropDown name="Gender"
            value={String(this.state.filters_gender)}
            doptions={this.state.genderOptions}
            hasIcons={false}
            minWidth={100}
            onDark={false}
            onChangeFunction={this.onDropGender}
            size="small"
            marginTop={8}
            marginLeft={2}
          />

          <DropDown name="Proximity"
            value={String(this.state.filters_proximity)}
            doptions={this.state.proximityOptions}
            hasIcons={false}
            minWidth={100}
            onDark={false}
            onChangeFunction={this.onDropProximity}
            size="small"
            marginTop={8}
            marginLeft={2}
          />

          {/* Counters & clear */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ color: '#e3e3e5' }}>
              Promotions: {this.props.promotionsCounter}
            </Typography>
            <Typography variant="body2" sx={{ color: '#e3e3e5' }}>
              Filters: {this.state.filtersCounter}
            </Typography>
            {this.state.filtersCounter > 0 && (
              <IconButton aria-label="Remove Filters" title="Remove Filters" onClick={this.resetFilters}>
                <Avatar alt="Remove Filters" sx={{ width: 18, height: 18, color: '#99302e' }}>
                  <HighlightOffIcon fontSize="small" />
                </Avatar>
              </IconButton>
            )}
          </Box>

          {/* Advanced info toggle (kept for parity) */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <ExpandMore expand={this.state.expanded} onClick={this.handleExpandClick}
              aria-expanded={this.state.expanded} aria-label="show more" sx={{ color: '#e3e3e5' }}>
              <ExpandMoreIcon />
            </ExpandMore>
          </Box>
        </Box>

        <Collapse in={this.state.expanded} timeout="auto" unmountOnExit>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              {/* Date preset (single select) */}
              <DropDown name="Date range"
                value={String(this.state.filters_datePreset)}
                doptions={this.state.datePresetOptions}
                hasIcons={false}
                minWidth={120}
                onDark={false}
                onChangeFunction={this.onDropDatePreset}
                size="small"
                marginTop={8}
                marginLeft={2}
              />

              {/* Custom period (visible only for CUSTOM) */}
              {this.state.filters_datePreset === 'CUSTOM' && (
                <>
                  <TextField
                    id="date-custom-from"
                    label="From"
                    type="date"
                    variant="standard"
                    value={this.state.customFrom}
                    onChange={this.onCustomFromChange}
                    sx={{ color: "#e3e3e5", alignSelf: "center", marginLeft: "16px" }}
                    InputLabelProps={{
                      shrink: true,
                      sx : { color: "#e3e3e5" }
                    }}
                    InputProps={{ sx : { color: "#e3e3e5" } }}
                  />
                  <TextField
                    id="date-custom-to"
                    label="To"
                    variant="standard"
                    type="date"
                    value={this.state.customTo}
                    onChange={this.onCustomToChange}
                    sx={{ color: "#e3e3e5", alignSelf: "center", marginLeft: "16px" }}
                    InputLabelProps={{
                      shrink: true,
                      sx : { color: "#e3e3e5" }
                    }}
                    InputProps={{ sx : { color: "#e3e3e5" } }}
                  />
                </>
              )}
            </Box>
          </CardContent>
        </Collapse>
      </Card>
    );
  }
}